import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography
} from '@mui/material';
import { put, get } from 'src/services';
import { authenticationService } from 'src/services/authentication.service';

interface EditBillingTaxTypeProps {
  itemData: any;
  handleClose: () => void;
  handleRefresh: () => void;
  showSnackbar: (message: string, severity: 'success' | 'error') => void;
}

const EditBillingTaxType: React.FC<EditBillingTaxTypeProps> = ({ itemData, handleClose, handleRefresh, showSnackbar }) => {
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const [loading, setLoading] = useState(false);
  const [taxesList, setTaxesList] = useState<any[]>([]);
  const [selectedTaxes, setSelectedTaxes] = useState<number[]>(itemData.taxes || []);
  
  const [formData, setFormData] = useState({
    id: itemData.id,
    name: itemData.name || '',
   exclusive:
    itemData.exclusive === true || itemData.exclusive === "true",
  status:
    itemData.status === true || itemData.status === "true",
  percentage:
    itemData.percentage === true || itemData.percentage === "true",
  vendorId,
  });

  useEffect(() => {
    fetchTaxes();
  }, []);

  const fetchTaxes = async () => {
    try {
      const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/tax/vendor/${vendorId}/getAllTaxesByVendor`);
      if (response && response.success && response.data) {
        setTaxesList(response.data);
      } else if (Array.isArray(response)) {
        setTaxesList(response);
      } else {
        setTaxesList([]);
      }
    } catch (error) {
      console.error('Error fetching taxes:', error);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [name]: event.target.checked }));
  };

  const handleTaxSelection = (taxId: number) => {
    setSelectedTaxes((prev) =>
      prev.includes(taxId)
        ? prev.filter((id) => id !== taxId)
        : [...prev, taxId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTaxes.length === taxesList.length) {
      setSelectedTaxes([]);
    } else {
      setSelectedTaxes(taxesList.map((tax) => tax.id));
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      showSnackbar('Please enter Name', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        taxes: selectedTaxes
      };

      const response = await put(`${process.env.REACT_APP_BASE_API_URL}/api/billing-tax-type/update`, payload);
      
      if (response && response.success) {
        showSnackbar('Billing tax type updated successfully', 'success');
        handleRefresh();
        handleClose();
      } else {
        showSnackbar(response?.message || 'Error updating billing tax type', 'error');
      }
    } catch (error) {
      console.error('Error updating:', error);
      showSnackbar('Error updating billing tax type', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Type/Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Type *"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter billing tax type name"
          />
        </Grid>
        <Grid item xs={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.percentage}
                  onChange={handleSwitchChange('percentage')}
                  name="percentage"
                  color="primary"
                />
              }
              label="Percentage"
            />
          </Grid>
         <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
         <FormControlLabel
  control={
    <Switch
      checked={formData.exclusive}
      onChange={handleSwitchChange("exclusive")}
      name="exclusive"
      color="primary"
    />
  }
  label="Exclusive"
/>
        </Grid>


       {/* Exclusive Switch */}
        <Grid item xs={12}>
         <FormControlLabel
  control={
    <Switch
      checked={formData.status}
      onChange={handleSwitchChange("status")}
      name="status"
      color="primary"
    />
  }
  label="Status"
/>
        </Grid>
          </Grid>
         </Grid>
        {/* Exclusive Switch */}
        
        
        {/* Taxes List Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Taxes List
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTaxes.length === taxesList.length && taxesList.length > 0}
                    indeterminate={selectedTaxes.length > 0 && selectedTaxes.length < taxesList.length}
                    onChange={handleSelectAll}
                  />
                }
                label="Select All"
              />
            </Box>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {taxesList.map((tax) => (
                  <ListItem key={tax.id} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTaxes.includes(tax.id)}
                          onChange={() => handleTaxSelection(tax.id)}
                        />
                      }
                      label={`${tax.taxType} (${tax.calculationOn})`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </Box>
    </Box>
  );
};

export default EditBillingTaxType;